import React, { useState, useMemo } from 'react';
import { format, differenceInDays, startOfMonth, endOfMonth, addMonths, eachDayOfInterval, startOfWeek, getWeek, min, max, addDays, subDays } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Edit2, Trash2, Archive, RotateCcw, GripVertical, Flag, ZoomIn, ZoomOut } from 'lucide-react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import ArchivedParentBadge from '@/components/common/ArchivedParentBadge';

export default function GanttChart({ 
  groupedActivities, 
  officers, 
  objectives,
  missions,
  expandedActivities,
  setExpandedActivities,
  handleEdit,
  deleteActivityMutation,
  archiveActivityMutation,
  unarchiveActivityMutation,
  updateKeyResultsStatusMutation,
  user,
  isCommander,
  eisenhowerLabels,
  groupByMode,
  provided,
  allActivities // optional: full unfiltered activities list for accurate capacity counts
}) {
  const [timelineZoom, setTimelineZoom] = useState(0); // -2 to +2, 0 is default

  // Calculate dynamic date range based on activities
  const { startDate, endDate, totalDays } = useMemo(() => {
    const today = new Date();
    let minDate = today;
    let maxDate = today;

    // Find earliest and latest dates from all activities
    Object.values(groupedActivities).flat().forEach(activity => {
      const activityStart = new Date(activity.reactivated_at || activity.created_date);
      const activityEnd = activity.due_date ? new Date(activity.due_date) : today;
      
      if (activityStart < minDate) minDate = activityStart;
      if (activityEnd > maxDate) maxDate = activityEnd;
    });

    // Add buffer based on zoom level
    const bufferWeeks = 2 + timelineZoom;
    const calculatedStart = subDays(startOfWeek(minDate), bufferWeeks * 7);
    const calculatedEnd = addDays(maxDate, bufferWeeks * 7);
    
    const days = eachDayOfInterval({ start: calculatedStart, end: calculatedEnd });
    
    return {
      startDate: calculatedStart,
      endDate: calculatedEnd,
      totalDays: days.length
    };
  }, [groupedActivities, timelineZoom]);

  const getActivityPosition = (activity) => {
    if (!activity.created_date) return { left: 0, width: 0 };
    
    const activityStart = new Date(activity.reactivated_at || activity.created_date);
    const activityEnd = activity.due_date ? new Date(activity.due_date) : new Date();
    
    const daysFromStart = differenceInDays(activityStart, startDate);
    const duration = differenceInDays(activityEnd, activityStart) || 1;
    
    const left = Math.max(0, (daysFromStart / totalDays) * 100);
    const width = Math.min((duration / totalDays) * 100, 100 - left);
    
    return { left: `${left}%`, width: `${width}%` };
  };

  // Generate week markers for timeline
  const weekMarkers = useMemo(() => {
    const weeks = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      const weekStart = startOfWeek(currentDate);
      const weekNumber = getWeek(weekStart);
      const monthYear = format(weekStart, 'MMM yyyy');
      weeks.push({ weekStart, weekNumber, monthYear });
      currentDate = addDays(currentDate, 7);
    }
    return weeks;
  }, [startDate, endDate]);

  return (
    <div className="space-y-8">
      {/* Timeline Controls */}
      <div className="flex items-center gap-2 justify-end">
        <span className="text-xs text-slate-400">Timeline Zoom:</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setTimelineZoom(Math.min(2, timelineZoom + 1))}
          disabled={timelineZoom === 2}
          className="h-7 border-orange-500/30 hover:bg-orange-500/10"
        >
          <ZoomOut className="w-3 h-3 text-orange-400" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setTimelineZoom(Math.max(-2, timelineZoom - 1))}
          disabled={timelineZoom === -2}
          className="h-7 border-orange-500/30 hover:bg-orange-500/10"
        >
          <ZoomIn className="w-3 h-3 text-orange-400" />
        </Button>
        <span className="text-xs text-slate-500">
          {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
        </span>
      </div>

      {Object.entries(groupedActivities).map(([groupEmail, groupActivities]) => {
        const groupOfficer = officers.find(o => o.user_email === groupEmail);
        const activityCount = groupActivities.length;
        const src = allActivities || Object.values(groupedActivities).flat();
        const liveCount = src.filter((a) => { const k = groupByMode === 'accountable' ? a.accountable_email : a.responsible_email; return k === groupEmail && a.status !== 'archived'; }).length;

        return (
          <div key={groupEmail} className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-white">
                  {groupOfficer ? `${groupOfficer.callsign} - ${groupOfficer.rank_title} (L${groupOfficer.clearance_level})` : groupEmail}
                </h2>
                <Badge variant="outline" className="text-slate-400 border-slate-700">
                  {liveCount} Live{activityCount !== liveCount ? ` (${activityCount} shown)` : ''} {liveCount === 1 ? 'Activity' : 'Activities'}
                </Badge>
              </div>
            </div>

            {/* Gantt Chart */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
              {/* Timeline Header */}
              <div className="bg-slate-950/50 border-b border-slate-800 p-4">
                <div className="flex">
                  <div className="w-64 flex-shrink-0" />
                  <div className="flex-1 relative h-16">
                    {/* Week markers */}
                    <div className="absolute inset-0">
                      {weekMarkers.map((week, i) => {
                        const weekStartDays = differenceInDays(week.weekStart, startDate);
                        const weekPosition = (weekStartDays / totalDays) * 100;
                        const weekWidth = (7 / totalDays) * 100;
                        
                        return (
                          <div
                            key={i}
                            className="absolute h-full border-l border-slate-700"
                            style={{ left: `${weekPosition}%`, width: `${weekWidth}%` }}
                          >
                            <div className="px-1">
                              <div className="text-xs font-semibold text-slate-400">
                                W{week.weekNumber}
                              </div>
                              <div className="text-xs text-slate-600">
                                {week.monthYear}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Today marker */}
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-indigo-500 z-10"
                      style={{ left: `${(differenceInDays(new Date(), startDate) / totalDays) * 100}%` }}
                    >
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs text-indigo-400 whitespace-nowrap">
                        Today
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activities */}
              <Droppable droppableId={groupEmail}>
                {(provided) => (
                  <div 
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="divide-y divide-slate-800"
                  >
                {groupActivities.map((activity, index) => {
                  const position = getActivityPosition(activity);
                  const objective = objectives.find(o => o.id === activity.objective_id);
                  const isExpanded = expandedActivities[activity.id];

                  return (
                    <Draggable key={activity.id} draggableId={activity.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex ${snapshot.isDragging ? 'bg-slate-800/50' : 'hover:bg-slate-800/30'} transition-colors`}
                        >
                          {/* Activity Info Column */}
                          <div className={`w-64 flex-shrink-0 p-2 border-r border-slate-800 ${
                            activity.key_results_status === 'green' ? 'bg-emerald-500 text-white' :
                            activity.key_results_status === 'yellow' ? 'bg-amber-500 text-white' :
                            activity.key_results_status === 'red' ? 'bg-red-500 text-white' :
                            activity.status === 'active' ? 'bg-emerald-500/20 text-slate-200' :
                            activity.status === 'pending_review' ? 'bg-amber-500/20 text-slate-200' :
                            activity.status === 'completed' ? 'bg-slate-500/20 text-slate-200' :
                            activity.status === 'archived' ? 'bg-slate-600/20 text-slate-500' :
                            'bg-blue-500/20 text-slate-200'
                          }`}>
                            <div className="flex items-start gap-1">
                              <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-200">
                                <GripVertical className="w-3 h-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2">
                                  <span className={`text-xs font-semibold ${
                                    activity.key_results_status === 'green' || activity.key_results_status === 'yellow' || activity.key_results_status === 'red'
                                      ? 'text-white/90'
                                      : 'text-indigo-400'
                                  }`}>
                                    {activity.activity_code || 'N/A'}
                                  </span>
                                  <span className="text-xs font-medium truncate">
                                    {activity.title}
                                  </span>
                                </div>
                                <div className="text-xs opacity-80 mt-0.5">
                                  {groupByMode === 'accountable' ? (
                                    <>
                                      R: {officers.find(o => o.user_email === activity.responsible_email)?.rank_title} {officers.find(o => o.user_email === activity.responsible_email)?.callsign || activity.responsible_email || 'TBD'}
                                    </>
                                  ) : (
                                    <>
                                      A: {officers.find(o => o.user_email === activity.accountable_email)?.rank_title} {officers.find(o => o.user_email === activity.accountable_email)?.callsign || activity.accountable_email}
                                    </>
                                  )}
                                </div>
                                <div className="flex gap-1 mt-1">
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    className="h-5 w-5" 
                                    onClick={() => setExpandedActivities(prev => ({...prev, [activity.id]: !prev[activity.id]}))}
                                  >
                                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  </Button>
                                  {((activity.accountable_email === user?.email) || (activity.responsible_email === user?.email) || isCommander) && (
                                    <>
                                      {activity.status === 'archived' ? (
                                        <Button 
                                          size="icon" 
                                          variant="ghost" 
                                          className="h-5 w-5" 
                                          onClick={() => unarchiveActivityMutation.mutate(activity.id)}
                                          title="Unarchive"
                                        >
                                          <RotateCcw className="w-3 h-3" />
                                        </Button>
                                      ) : (
                                        <Button 
                                          size="icon" 
                                          variant="ghost" 
                                          className="h-5 w-5" 
                                          onClick={() => archiveActivityMutation.mutate(activity.id)}
                                          title="Archive"
                                        >
                                          <Archive className="w-3 h-3" />
                                        </Button>
                                      )}
                                      <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => handleEdit(activity)}>
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                      <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => deleteActivityMutation.mutate(activity.id)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Timeline Column */}
                          <div className="flex-1 p-2 relative">
                            {/* Grid lines */}
                            <div className="absolute inset-0">
                              {weekMarkers.map((week, i) => {
                                const weekStartDays = differenceInDays(week.weekStart, startDate);
                                const weekPosition = (weekStartDays / totalDays) * 100;
                                
                                return (
                                  <div
                                    key={i}
                                    className="absolute h-full border-l border-slate-800/50"
                                    style={{ left: `${weekPosition}%` }}
                                  />
                                );
                              })}
                            </div>
                            
                            {/* Activity bar */}
                            <div className="relative h-5">
                              <div
                                className={`absolute h-5 rounded flex items-center px-2 ${
                                  activity.key_results_status === 'green' ? 'bg-emerald-500' :
                                  activity.key_results_status === 'yellow' ? 'bg-amber-500' :
                                  activity.key_results_status === 'red' ? 'bg-red-500' :
                                  activity.status === 'active' ? 'bg-emerald-500/60' :
                                  activity.status === 'pending_review' ? 'bg-amber-500/60' :
                                  activity.status === 'completed' ? 'bg-slate-500/60' :
                                  'bg-indigo-500/60'
                                }`}
                                style={{ left: position.left, width: position.width, minWidth: '20px' }}
                              >
                                {activity.due_date && (
                                  <div className="text-xs text-white font-semibold whitespace-nowrap">
                                    {format(new Date(activity.due_date), 'MMM d')}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Expanded details */}
                            {isExpanded && (
                              <div className="mt-4 space-y-3 text-sm">
                                {objective && objective.status === 'archived' && (
                                  <ArchivedParentBadge 
                                    parentType="Objective"
                                    parentName={objective.statement}
                                    parentPage="discovery"
                                  />
                                )}
                                {objective && (() => {
                                  const mission = missions?.find(m => m.id === objective.mission_id);
                                  return mission && mission.status === 'archived' ? (
                                    <ArchivedParentBadge 
                                      parentType="Mission"
                                      parentName={mission.title}
                                      parentPage="missions"
                                    />
                                  ) : null;
                                })()}
                                {objective && (
                                  <div>
                                    <div className="text-xs text-slate-500 font-semibold mb-1">OBJECTIVE</div>
                                    <div className="text-slate-300">
                                      {objective.objective_code && <span className="text-indigo-400">[{objective.objective_code}]</span>} {objective.statement}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Key Results */}
                                {(activity.key_results || []).length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-xs text-slate-500 font-semibold">KEY RESULTS</div>
                                      {activity.accountable_email === user?.email && (
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => updateKeyResultsStatusMutation.mutate({ activityId: activity.id, status: 'green' })}
                                            className={`p-1 rounded ${activity.key_results_status === 'green' ? 'bg-emerald-500' : 'bg-emerald-500/30 hover:bg-emerald-500/50'}`}
                                          >
                                            <Flag className="w-3 h-3 text-white" />
                                          </button>
                                          <button
                                            onClick={() => updateKeyResultsStatusMutation.mutate({ activityId: activity.id, status: 'yellow' })}
                                            className={`p-1 rounded ${activity.key_results_status === 'yellow' ? 'bg-amber-500' : 'bg-amber-500/30 hover:bg-amber-500/50'}`}
                                          >
                                            <Flag className="w-3 h-3 text-white" />
                                          </button>
                                          <button
                                            onClick={() => updateKeyResultsStatusMutation.mutate({ activityId: activity.id, status: 'red' })}
                                            className={`p-1 rounded ${activity.key_results_status === 'red' ? 'bg-red-500' : 'bg-red-500/30 hover:bg-red-500/50'}`}
                                          >
                                            <Flag className="w-3 h-3 text-white" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <div className="space-y-2">
                                      {activity.key_results.map((kr, idx) => {
                                        const current = parseFloat(kr.current_value) || 0;
                                        const target = parseFloat(kr.target) || 1;
                                        const progress = Math.min((current / target) * 100, 100);
                                        return (
                                          <div key={idx}>
                                            <div className="flex justify-between text-xs mb-1">
                                              <span className="text-slate-300">{kr.description}</span>
                                              <span className="text-slate-400">{kr.current_value}/{kr.target} {kr.unit}</span>
                                            </div>
                                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                                              <div
                                                className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
                                                style={{ width: `${progress}%` }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        );
      })}
    </div>
  );
}